import React, { useState, useEffect } from "react";
import {
  Box,
  Paper,
  Typography,
  Button,
  CircularProgress,
  TextField,
  Alert,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TableSortLabel,
} from "@mui/material";
import Cron from "react-cron-generator";
import "react-cron-generator/build/cron-builder.css";
import { useTheme } from "@mui/material/styles";
import CronBuilderBox from "../../../components/CronBuilderBox";
import { getMutedPanelSx } from "../../../theme/panelStyles";
import { ritualsApi } from "../../../services/api/omni/rituals";

const OutletTab = () => {
  const theme = useTheme();
  const [cronValue, setCronValue] = useState("0 * * * *");
  const [discordWebhook, setDiscordWebhook] = useState("");
  const [cronStatus, setCronStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [scanLoading, setScanLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [products, setProducts] = useState([]);
  const [productsLoading, setProductsLoading] = useState(false);

  // Pagination & Search & Sort State
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(50);
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState("updated_at");
  const [sortOrder, setSortOrder] = useState("desc");
  const [totalItems, setTotalItems] = useState(0);

  const fetchStatus = async () => {
    try {
      setLoading(true);
      const response = await ritualsApi.getRitualOutletsStatus();
      setCronStatus(response.data);
      if (response.data.expression) {
        setCronValue(response.data.expression);
      }
      if (response.data.arguments && response.data.arguments.discord_webhook) {
        setDiscordWebhook(response.data.arguments.discord_webhook);
      }
      setError(null);
    } catch (err) {
      console.error("Failed to fetch status", err);
      // setError("Konnte Status nicht laden."); // Optional: silent fail on init
    } finally {
      setLoading(false);
    }
  };

  const fetchProducts = async () => {
    try {
      setProductsLoading(true);
      // Backend expects 1-based page index
      const response = await ritualsApi.getRitualsProducts(
        page + 1,
        pageSize,
        search,
        sortBy,
        sortOrder,
      );
      setProducts(response.data.items);
      setTotalItems(response.data.total);
    } catch (err) {
      console.error("Failed to fetch products", err);
    } finally {
      setProductsLoading(false);
    }
  };

  useEffect(() => {
    fetchProducts();
  }, [page, pageSize, search, sortBy, sortOrder]);

  const handleSort = (property) => {
    const isAsc = sortBy === property && sortOrder === "asc";
    setSortOrder(isAsc ? "desc" : "asc");
    setSortBy(property);
  };

  useEffect(() => {
    fetchStatus();
    fetchProducts();
  }, []);

  const startCron = async () => {
    try {
      setLoading(true);
      setError(null);
      setSuccess(null);
      await ritualsApi.startRitualOutletMonitor(cronValue, discordWebhook);
      setSuccess("Cron successfully started/updated.");
      await fetchStatus();
    } catch (err) {
      console.error(err);
      setError(err.response?.data?.detail || "Error starting the cron.");
    } finally {
      setLoading(false);
    }
  };

  const stopCron = async () => {
    try {
      setLoading(true);
      setError(null);
      setSuccess(null);
      await ritualsApi.stopRitualOutletMonitor();
      setSuccess("Cron successfully stopped.");
      await fetchStatus();
    } catch (err) {
      console.error(err);
      setError(err.response?.data?.detail || "Error stopping the cron.");
    } finally {
      setLoading(false);
    }
  };

  const runManualScan = async () => {
    try {
      setScanLoading(true);
      setError(null);
      setSuccess(null);
      const response = await ritualsApi.runManualScan(discordWebhook);
      setSuccess(
        `Manual scan completed. Processed ${response.data.items_processed} items.`,
      );
      await fetchProducts();
    } catch (err) {
      console.error(err);
      setError(err.response?.data?.detail || "Error running manually.");
    } finally {
      setScanLoading(false);
    }
  };

  return (
    <Box p={3}>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography variant="h6" mb={2}>
          Rituals Outlet Monitor Configuration
        </Typography>

        <Box mb={3}>
          <TextField
            fullWidth
            label="Discord Webhook URL"
            variant="outlined"
            value={discordWebhook}
            onChange={(e) => setDiscordWebhook(e.target.value)}
            helperText="Optional: URL for Discord notifications on changes."
          />
        </Box>

        <Divider sx={{ my: 2 }} />

        <CronBuilderBox mb={2}>
          <Typography variant="subtitle1" mb={1}>
            Set Schedule:
          </Typography>
          <Cron
            onChange={(e) => setCronValue(e)}
            value={cronValue}
            showResultText={true}
            showResultCron={true}
            locale="en"
          />
        </CronBuilderBox>

        <Box display="flex" alignItems="center" gap={2} flexWrap="wrap" mt={2}>
          <Button
            variant="contained"
            color="primary"
            onClick={startCron}
            disabled={loading}
          >
            Start / Update Monitor
          </Button>
          <Button
            variant="outlined"
            color="error"
            onClick={stopCron}
            disabled={loading}
          >
            Stop Monitor
          </Button>
          <Button
            variant="contained"
            color="secondary"
            onClick={runManualScan}
            disabled={scanLoading}
          >
            {scanLoading ? "Scanning..." : "Run Manual Scan Now"}
          </Button>

          {loading && <CircularProgress size={24} />}
        </Box>

        {cronStatus && (
          <Box sx={getMutedPanelSx(theme)}>
            <Typography variant="subtitle2" gutterBottom>
              Status
            </Typography>
            <Typography variant="body2">
              <strong>Status:</strong>{" "}
              {cronStatus.active ? (
                <span style={{ color: "green" }}>Active</span>
              ) : (
                <span style={{ color: "red" }}>Inactive</span>
              )}
            </Typography>
            <Typography variant="body2">
              <strong>Current Cron:</strong> {cronStatus.expression || "N/A"}
            </Typography>
            {cronStatus.next_run_at && (
              <Typography variant="body2">
                <strong>Next Run:</strong>{" "}
                {cronStatus.active
                  ? new Date(cronStatus.next_run_at).toLocaleString()
                  : "-"}
              </Typography>
            )}
            {cronStatus.last_run_at && (
              <Typography variant="body2">
                <strong>Last Run:</strong>{" "}
                {new Date(cronStatus.last_run_at).toLocaleString()}
              </Typography>
            )}

            <Typography variant="body2" mt={1}>
              <strong>Configured Webhook:</strong>{" "}
              {cronStatus.arguments?.discord_webhook ? "Yes (Hidden)" : "No"}
            </Typography>
          </Box>
        )}
      </Paper>

      <Paper sx={{ p: 2 }}>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={2}
          flexWrap="wrap"
          gap={2}
        >
          <Typography variant="h6">Scraped Products</Typography>
          <Box display="flex" gap={2}>
            <TextField
              size="small"
              label="Search products..."
              variant="outlined"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(0); // Reset to first page on search
              }}
            />
            <Button
              onClick={fetchProducts}
              disabled={productsLoading}
              size="small"
              variant="outlined"
            >
              Refresh
            </Button>
          </Box>
        </Box>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Data-ID</TableCell>
                <TableCell>
                  <TableSortLabel
                    active={sortBy === "name"}
                    direction={sortBy === "name" ? sortOrder : "asc"}
                    onClick={() => handleSort("name")}
                  >
                    Name
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={sortBy === "sales_price"}
                    direction={sortBy === "sales_price" ? sortOrder : "asc"}
                    onClick={() => handleSort("sales_price")}
                  >
                    Price
                  </TableSortLabel>
                </TableCell>
                <TableCell>Old Price</TableCell>
                <TableCell>Stock</TableCell>
                <TableCell>Category</TableCell>
                <TableCell>
                  <TableSortLabel
                    active={sortBy === "updated_at"}
                    direction={sortBy === "updated_at" ? sortOrder : "asc"}
                    onClick={() => handleSort("updated_at")}
                  >
                    Last Seen
                  </TableSortLabel>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {productsLoading ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <CircularProgress size={24} />
                  </TableCell>
                </TableRow>
              ) : products.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    No products found. Run a scan.
                  </TableCell>
                </TableRow>
              ) : (
                products.map((p) => (
                  <TableRow key={p.product_id}>
                    <TableCell>{p.product_id}</TableCell>
                    <TableCell>
                      {p.url ? (
                        <a
                          href={p.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ textDecoration: "none", color: "inherit" }}
                        >
                          <Typography
                            variant="body2"
                            sx={{
                              "&:hover": { textDecoration: "underline" },
                              color: "primary.main",
                              fontWeight: 500,
                            }}
                          >
                            {p.name}
                          </Typography>
                        </a>
                      ) : (
                        p.name
                      )}
                    </TableCell>
                    <TableCell>{p.sales_price} €</TableCell>
                    <TableCell>
                      {p.original_price ? `${p.original_price} €` : "-"}
                    </TableCell>
                    <TableCell>{p.in_stock}</TableCell>
                    <TableCell>{p.item_category}</TableCell>
                    <TableCell>
                      {p.last_seen_at
                        ? new Date(p.last_seen_at).toLocaleString()
                        : "-"}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={totalItems}
          page={page}
          onPageChange={(e, newPage) => setPage(newPage)}
          rowsPerPage={pageSize}
          onRowsPerPageChange={(e) => {
            setPageSize(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[10, 25, 50, 100]}
        />
      </Paper>
    </Box>
  );
};

export default OutletTab;
